import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { carsysApi } from '../api/carsysApi';

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [klanten, setKlanten] = useState([]);
  const [klantAutos, setKlantAutos] = useState([]);
  const [bonnen, setBonnen] = useState([]);
  const [betalingen, setBetalingen] = useState([]);
  const [kosten, setKosten] = useState([]);
  const [handelAutos, setHandelAutos] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [onderdelen, setOnderdelen] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [inkoopOrders, setInkoopOrders] = useState([]);
  const [garantieclaims, setGarantieclaims] = useState([]);
  const [documenten, setDocumenten] = useState([]);
  const [meldingen, setMeldingen] = useState([]);
  const [gegevens, setGegevens] = useState({});
  const [stats, setStats] = useState({ klanten: 0, voertuigen: 0, bonnen: 0, openstaand: 0, openstaandBedrag: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshAll = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [k, ka, b, bt, ha, pl, od, dc, ml, gg, st] = await Promise.all([
        carsysApi.getKlanten(), carsysApi.getKlantAutos(), carsysApi.getBonnen(),
        carsysApi.getBetalingen(), carsysApi.getHandelAutos(), carsysApi.getPlanning(),
        carsysApi.getOnderdelen(), carsysApi.getDocumenten(), carsysApi.getMeldingen(),
        carsysApi.getGegevens(), carsysApi.getDashboardStats(),
      ]);
      setKlanten(k); setKlantAutos(ka); setBonnen(b); setBetalingen(bt);
      setHandelAutos(ha); setPlanning(pl); setOnderdelen(od);
      setDocumenten(dc); setMeldingen(ml); setGegevens(gg); setStats(st);
      // Kosten apart laden zodat een eventuele fout de rest niet blokkeert
      carsysApi.getKosten().then(setKosten).catch(() => setKosten([]));
      // Epic G — inkoop & leveranciers (los laden zodat een fout de rest niet blokkeert)
      carsysApi.getLeveranciers().then(setLeveranciers).catch(() => setLeveranciers([]));
      carsysApi.getInkoopOrders().then(setInkoopOrders).catch(() => setInkoopOrders([]));
      carsysApi.getGarantieclaims().then(setGarantieclaims).catch(() => setGarantieclaims([]));
      setError(null);
    } catch (e) {
      console.error('CarSys data load failed', e);
      setError(e.message || 'Kon data niet laden');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Centrale helper: na delete updaten we dashboard stats én meldingen,
  // zodat KPI tiles én APK/Betaling/Slapend alerts gelijk meelopen.
  const syncAfterDelete = useCallback(() => {
    carsysApi.getDashboardStats().then(setStats).catch(() => {});
    carsysApi.refreshMeldingen()
      .then(() => carsysApi.getMeldingen().then(setMeldingen))
      .catch(() => {});
  }, []);

  // Mutators (event handlers) - use .then() chain instead of async/await to satisfy React 19 set-state-in-effect rule
  const createKlant = useCallback((data) => carsysApi.createKlant(data).then(r => { setKlanten(prev => [r, ...prev]); return r; }), []);
  const updateKlant = useCallback((id, data) => carsysApi.updateKlant(id, data).then(r => { setKlanten(prev => prev.map(k => k.id === id ? r : k)); return r; }), []);
  const deleteKlant = useCallback((id) => carsysApi.deleteKlant(id).then(() => {
    setKlanten(prev => prev.filter(k => k.id !== id));
    // Cascade: backend verwijdert ook klant_autos. Verwijder ze lokaal ook.
    setKlantAutos(prev => prev.filter(a => a.klantId !== id));
    syncAfterDelete();
  }), [syncAfterDelete]);

  const createKlantAuto = useCallback((data) => carsysApi.createKlantAuto(data).then(r => {
    setKlantAutos(prev => [...prev, r]);
    if (r.klantId) setKlanten(prev => prev.map(k => k.id === r.klantId ? { ...k, kentekens: [...new Set([...(k.kentekens || []), r.kenteken])] } : k));
    return r;
  }), []);
  const updateKlantAuto = useCallback((kenteken, data) => carsysApi.updateKlantAuto(kenteken, data).then(r => {
    setKlantAutos(prev => prev.map(a => a.kenteken === kenteken ? r : a));
    return r;
  }), []);
  const setPrimaryKlantAuto = useCallback((kenteken) => carsysApi.setPrimaryKlantAuto(kenteken).then(r => {
    // Update lokaal: zet isHoofd=true op deze, false op alle andere voertuigen van dezelfde klant
    setKlantAutos(prev => prev.map(a => {
      if (a.kenteken === kenteken) return { ...a, isHoofd: true };
      if (a.klantId === r.klantId) return { ...a, isHoofd: false };
      return a;
    }));
    return r;
  }), []);
  const archiveerKlantAuto = useCallback((kenteken, data) => carsysApi.archiveerKlantAuto(kenteken, data).then(r => {
    setKlantAutos(prev => prev.map(a => a.kenteken === r.kenteken ? r : a));
    return r;
  }), []);
  const herstelKlantAuto = useCallback((kenteken) => carsysApi.herstelKlantAuto(kenteken).then(r => {
    setKlantAutos(prev => prev.map(a => a.kenteken === r.kenteken ? r : a));
    return r;
  }), []);
  const deleteKlantAuto = useCallback((kenteken) => carsysApi.deleteKlantAuto(kenteken).then(() => {
    setKlantAutos(prev => prev.filter(a => a.kenteken !== kenteken));
    setKlanten(prev => prev.map(k => ({ ...k, kentekens: (k.kentekens || []).filter(kt => kt !== kenteken) })));
    syncAfterDelete();
  }), [syncAfterDelete]);

  const createBon = useCallback((data) => carsysApi.createBon(data).then(r => { setBonnen(prev => [r, ...prev]); return r; }), []);
  const updateBon = useCallback((id, data) => carsysApi.updateBon(id, data).then(r => { setBonnen(prev => prev.map(b => b.id === id ? r : b)); return r; }), []);
  const setBonStatus = useCallback((id, payload) => carsysApi.setBonStatus(id, payload).then(r => { setBonnen(prev => prev.map(b => b.id === id ? r : b)); return r; }), []);
  const convertBon = useCallback((id, target) => carsysApi.convertBon(id, target).then(r => { setBonnen(prev => [r, ...prev]); return r; }), []);
  const deleteBon = useCallback((id) => carsysApi.deleteBon(id).then(() => {
    setBonnen(prev => prev.filter(b => b.id !== id));
    syncAfterDelete();
  }), [syncAfterDelete]);

  const createPlanning = useCallback((data) => carsysApi.createPlanning(data).then(r => { setPlanning(prev => [...prev, r]); return r; }), []);
  const updatePlanning = useCallback((id, data) => carsysApi.updatePlanning(id, data).then(r => { setPlanning(prev => prev.map(p => p.id === id ? r : p)); return r; }), []);
  const deletePlanning = useCallback((id) => carsysApi.deletePlanning(id).then(() => {
    setPlanning(prev => prev.filter(p => p.id !== id));
    syncAfterDelete();
  }), [syncAfterDelete]);
  const notifyPlanning = useCallback((id, payload) => carsysApi.notifyPlanning(id, payload), []);

  const createOnderdeel = useCallback((data) => carsysApi.createOnderdeel(data).then(r => { setOnderdelen(prev => [r, ...prev]); return r; }), []);
  const updateOnderdeel = useCallback((id, data) => carsysApi.updateOnderdeel(id, data).then(r => { setOnderdelen(prev => prev.map(o => o.id === id ? r : o)); return r; }), []);
  const deleteOnderdeel = useCallback((id) => carsysApi.deleteOnderdeel(id).then(() => {
    setOnderdelen(prev => prev.filter(o => o.id !== id));
    syncAfterDelete();
  }), [syncAfterDelete]);

  const createDocument = useCallback((data) => carsysApi.createDocument(data).then(r => { setDocumenten(prev => [r, ...prev]); return r; }), []);
  const deleteDocument = useCallback((id) => carsysApi.deleteDocument(id).then(() => setDocumenten(prev => prev.filter(d => d.id !== id))), []);

  const refreshStats = useCallback(() => carsysApi.getDashboardStats().then(st => { setStats(st); return st; }), []);

  const refreshMeldingen = useCallback(() => carsysApi.refreshMeldingen().then(r => carsysApi.getMeldingen().then(ml => { setMeldingen(ml); return r; })), []);

  const createBetaling = useCallback((data) => carsysApi.createBetaling(data).then(r => {
    setBetalingen(prev => [r, ...prev]);
    setBonnen(prev => prev.map(b => b.id === data.bonId ? { ...b, betaald: (b.betaald || 0) + data.bedrag, openstaand: Math.max(0, (b.openstaand || 0) - data.bedrag), status: (b.openstaand || 0) - data.bedrag <= 0 ? 'Betaald' : 'DeelsBetaald' } : b));
    return r;
  }), []);

  const updateBetaling = useCallback((id, data) => carsysApi.updateBetaling(id, data).then(r => {
    setBetalingen(prev => prev.map(b => b.id === id ? r : b));
    // Indien bedrag veranderd: bon refresh ophalen voor consistentie
    carsysApi.getBonnen().then(setBonnen).catch(() => {});
    return r;
  }), []);

  const deleteBetaling = useCallback((id) => {
    return carsysApi.deleteBetaling(id).then(() => {
      setBetalingen(prev => {
        const removed = prev.find(b => b.id === id);
        if (removed) {
          setBonnen(prevB => prevB.map(b => b.id === removed.bonId ? {
            ...b,
            betaald: Math.max(0, (b.betaald || 0) - (removed.bedrag || 0)),
            openstaand: Math.min(b.totaal || 0, (b.openstaand || 0) + (removed.bedrag || 0)),
            status: Math.min(b.totaal || 0, (b.openstaand || 0) + (removed.bedrag || 0)) > 0
              ? (Math.max(0, (b.betaald || 0) - (removed.bedrag || 0)) > 0 ? 'DeelsBetaald' : 'Gefactureerd')
              : 'Betaald',
          } : b));
        }
        return prev.filter(b => b.id !== id);
      });
      carsysApi.getDashboardStats().then(setStats).catch(() => {});
      carsysApi.refreshMeldingen()
        .then(() => carsysApi.getMeldingen().then(setMeldingen))
        .catch(() => {});
    });
  }, []);

  const createHandelAuto = useCallback((data) => carsysApi.createHandelAuto(data).then(r => { setHandelAutos(prev => [r, ...prev]); return r; }), []);
  const updateHandelAuto = useCallback((id, data) => carsysApi.updateHandelAuto(id, data).then(r => { setHandelAutos(prev => prev.map(a => a.id === id ? r : a)); return r; }), []);
  const deleteHandelAuto = useCallback((id) => carsysApi.deleteHandelAuto(id).then(() => {
    setHandelAutos(prev => prev.filter(a => a.id !== id));
    syncAfterDelete();
  }), [syncAfterDelete]);

  const updateGegevens = useCallback((data) => carsysApi.updateGegevens(data).then(r => { setGegevens(r); return r; }), []);

  const createKost = useCallback((data) => carsysApi.createKost(data).then(r => { setKosten(prev => [r, ...prev]); return r; }), []);
  const updateKost = useCallback((id, data) => carsysApi.updateKost(id, data).then(r => { setKosten(prev => prev.map(k => k.id === id ? r : k)); return r; }), []);
  const deleteKost = useCallback((id) => carsysApi.deleteKost(id).then(() => { setKosten(prev => prev.filter(k => k.id !== id)); }), []);

  // Epic G — Inkoop & leveranciers
  const createLeverancier = useCallback((data) => carsysApi.createLeverancier(data).then(r => { setLeveranciers(prev => [r, ...prev]); return r; }), []);
  const updateLeverancier = useCallback((id, data) => carsysApi.updateLeverancier(id, data).then(r => { setLeveranciers(prev => prev.map(l => l.id === id ? r : l)); return r; }), []);
  const deleteLeverancier = useCallback((id) => carsysApi.deleteLeverancier(id).then(() => setLeveranciers(prev => prev.filter(l => l.id !== id))), []);

  const createInkoopOrder = useCallback((data) => carsysApi.createInkoopOrder(data).then(r => { setInkoopOrders(prev => [r, ...prev]); return r; }), []);
  const updateInkoopOrder = useCallback((id, data) => carsysApi.updateInkoopOrder(id, data).then(r => { setInkoopOrders(prev => prev.map(o => o.id === id ? r : o)); return r; }), []);
  const deleteInkoopOrder = useCallback((id) => carsysApi.deleteInkoopOrder(id).then(() => setInkoopOrders(prev => prev.filter(o => o.id !== id))), []);
  const setInkoopOrderStatus = useCallback((id, payload) => carsysApi.setInkoopOrderStatus(id, payload).then(r => { setInkoopOrders(prev => prev.map(o => o.id === id ? r : o)); return r; }), []);
  const ontvangstInkoopOrder = useCallback((id, payload) => carsysApi.ontvangstInkoopOrder(id, payload).then(r => {
    setInkoopOrders(prev => prev.map(o => o.id === id ? r : o));
    // Voorraad + kosten kunnen gewijzigd zijn: ververs onderdelen en kosten
    carsysApi.getOnderdelen().then(setOnderdelen).catch(() => {});
    if (r.kostenpostAangemaakt) carsysApi.getKosten().then(setKosten).catch(() => {});
    return r;
  }), []);

  const createGarantieclaim = useCallback((data) => carsysApi.createGarantieclaim(data).then(r => { setGarantieclaims(prev => [r, ...prev]); return r; }), []);
  const updateGarantieclaim = useCallback((id, data) => carsysApi.updateGarantieclaim(id, data).then(r => { setGarantieclaims(prev => prev.map(c => c.id === id ? r : c)); return r; }), []);
  const deleteGarantieclaim = useCallback((id) => carsysApi.deleteGarantieclaim(id).then(() => setGarantieclaims(prev => prev.filter(c => c.id !== id))), []);

  const value = {
    klanten, klantAutos, bonnen, betalingen, handelAutos, planning,
    onderdelen, documenten, meldingen, gegevens, stats, loading, error,
    kosten,
    leveranciers, inkoopOrders, garantieclaims,
    refreshAll,
    createKlant, updateKlant, deleteKlant,
    createKlantAuto, updateKlantAuto, deleteKlantAuto, setPrimaryKlantAuto, archiveerKlantAuto, herstelKlantAuto,
    createBon, updateBon, setBonStatus, convertBon, deleteBon,
    createBetaling, updateBetaling, deleteBetaling,
    createHandelAuto, updateHandelAuto, deleteHandelAuto,
    createKost, updateKost, deleteKost,
    createLeverancier, updateLeverancier, deleteLeverancier,
    createInkoopOrder, updateInkoopOrder, deleteInkoopOrder, setInkoopOrderStatus, ontvangstInkoopOrder,
    createGarantieclaim, updateGarantieclaim, deleteGarantieclaim,
    createPlanning, updatePlanning, deletePlanning, notifyPlanning,
    createOnderdeel, updateOnderdeel, deleteOnderdeel,
    createDocument, deleteDocument,
    refreshMeldingen,
    refreshStats,
    updateGegevens,
    rdwLookup: carsysApi.rdwLookup,
    vinLookup: carsysApi.vinLookup,
    adresLookup: carsysApi.adresLookup,
    sendEmail: carsysApi.sendEmail,
    getAudit: carsysApi.getAudit,
    getMailLog: carsysApi.getMailLog,
    backupUrl: carsysApi.backupUrl,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside DataProvider');
  return ctx;
}
